import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useEffect } from "react";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";
import { ROLE_LIST_WINDOW_QUERY } from "../../api/RoleListWindowQuery";

type ListRolesResponse =
  paths["/roles"]["get"]["responses"]["200"]["content"]["application/json"];

export type RoleOption = Pick<ListRolesResponse["items"][number], "id" | "name">;

// Roles for the list's role filter dropdown. Failure degrades to an empty list
// (the filter simply has no options); the user list itself still loads.
export function useRoleOptions(): RoleOption[] {
  const [roles, setRoles] = useState<RoleOption[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetchWithRefresh(`/api/roles?${ROLE_LIST_WINDOW_QUERY}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = (await response.json()) as ListRolesResponse;
        if (!cancelled) {
          setRoles(data.items.map((role) => ({ id: role.id, name: role.name })));
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to fetch role options");
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return roles;
}
